/*
 * Optimized Critical JS - Performance Enhanced
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;
    
    const template = this.querySelector(':scope > template[shadowrootmode="open"]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const shadow = this.attachShadow({ mode: 'open' });
    shadow.append(template.content.cloneNode(true));
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  constructor(callback) {
    super((entries) => {
      if (!this.#initialized) {
        this.#initialized = true;
        return;
      }
      callback(entries, this);
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 */
export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 */
export class OverflowList extends DeclarativeShadowElement {
  static observedAttributes = ['disabled', 'minimum-items'];

  #refs;
  #scheduled = false;
  #lastDimensions = { width: null, height: null };
  #resizeObserver;
  #mutationObserver;

  constructor() {
    super();
    // Initialize observers in constructor for better performance
    this.#resizeObserver = new ResizeNotifier(this.#handleChange);
    this.#mutationObserver = new MutationObserver(this.#handleChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'disabled' || oldValue === newValue) return;
    
    newValue === 'true' ? this.#reset() : this.#reflowItems();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      this.#deferredInitialize();
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Use microtask instead of setTimeout for faster execution
      queueMicrotask(() => this.#initialize());
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #deferredInitialize() {
    const init = () => {
      this.removeAttribute('defer');
      this.#initialize();
    };

    const idleCallback = window.requestIdleCallback || setTimeout;
    idleCallback(() => this.schedule(init), { timeout: 100 });
  }

  #initialize() {
    const { shadowRoot } = this;
    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = { defaultSlot, overflowSlot, moreSlot, overflow, list, placeholder };

    // Use passive event listener for better scroll performance
    this.addEventListener('reflow', (event) => {
      this.#reflowItems(event.detail.lastVisibleElement);
    }, { passive: true });

    this.#reflowItems();
  }

  get schedule() {
    return Theme?.utilities?.scheduler?.schedule || 
      ((callback) => requestAnimationFrame(() => queueMicrotask(callback)));
  }

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    return this.#refs.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs.defaultSlot;
  }

  #handleChange = (entries) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    // Optimized loop - break early if not ResizeObserverEntry
    for (const entry of entries) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      
      isResize = true;
      const { width: w, height: h } = entry.contentRect;
      width = Math.round(w);
      height = Math.round(h);
      break; // Only process first entry
    }

    // Skip if no dimensions or same as last
    if (isResize) {
      if (!width || !height || 
          (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        return;
      }
      this.#lastDimensions = { width, height };
    }

    this.#scheduled = true;
    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;
    const elements = overflowSlot.assignedElements();
    
    // Batch DOM updates
    for (const element of elements) {
      element.slot = defaultSlot.name;
    }
  }

  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  #updateMinimumReached(visibleElements) {
    const minimum = this.minimumItems;
    if (minimum === null) return;

    const minimumReached = visibleElements.length < minimum;
    
    if (minimumReached) {
      this.setAttribute('minimum-reached', '');
    } else {
      this.removeAttribute('minimum-reached');
    }

    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }

  showAll() {
    const { placeholder } = this.#refs;
    placeholder.style.cssText = 'width: 0; display: none;'; // Batch style updates
    this.setAttribute('disabled', 'true');
  }

  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Get height once and cache
    const height = this.firstElementChild?.getBoundingClientRect().height;

    if (height) {
      list.style.cssText = `height: ${height}px; overflow: hidden;`; // Batch updates
    }

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Hide both at once
    moreSlot.hidden = true;
    placeholder.hidden = true;

    const rootRect = list.getBoundingClientRect();
    const rootTop = rootRect.top;

    // Cache dimensions
    this.#lastDimensions = { 
      width: Math.round(rootRect.width), 
      height: Math.round(rootRect.height) 
    };

    // Optimized visible element detection
    const getVisibleElements = () => elements.filter(el => el.getBoundingClientRect().top <= rootTop);
    let visibleElements = getVisibleElements();

    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Batch style updates
      moreSlot.style.cssText = `order: -1; height: ${height}px;`;
      moreSlot.hidden = false;

      if (lastVisibleElement) {
        lastVisibleElement.style.order = '-1';
      }

      visibleElements = getVisibleElements();

      // Reset order in batch
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          visibleElements.splice(visibleIndex, 1);
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.height = 'auto';
    }

    // Use Set for faster lookup
    const visibleSet = new Set(visibleElements);
    const overflowingElements = elements.filter(el => !visibleSet.has(el));
    const hasOverflow = overflowingElements.length > 0;

    // Batch slot assignments
    const overflowSlotName = overflowSlot.name;
    const defaultSlotName = defaultSlot.name;
    
    for (const element of elements) {
      element.slot = visibleSet.has(element) ? defaultSlotName : overflowSlotName;
    }

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      const placeholderWidth = overflowingElements[0]?.clientWidth || 0;
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    const overflowCount = overflowingElements.length;
    list.style.counterReset = `overflow-count ${overflowCount}`;
    this.style.setProperty('--overflow-count', `${overflowCount}`);
    list.style.removeProperty('overflow');

    if (hasOverflow) {
      this.#updateMinimumReached(visibleElements);
    }

    // Re-observe
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };
}

// Define custom element if not already defined
if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Optimized header height calculation
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  const length = children.length;

  for (let i = 0; i < length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // Check for transparent header
  if (header instanceof HTMLElement && 
      header.hasAttribute('transparent') && 
      header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Optimized header height updates with caching
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');
  if (!(header instanceof HTMLElement)) return;

  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  // Batch CSS custom property updates
  const style = document.body.style;
  style.setProperty('--header-height', `${headerHeight}px`);
  style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

/**
 * Optimized transparent header offset calculation
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  
  // Early exit optimization
  if (!header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  
  if (!hasHeaderSection) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
  const shouldApplyOffset = hasImmediateSection ? '0' : '1';
  
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

/**
 * Batch update all header custom properties
 */
export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Use requestAnimationFrame for smoother initial load
requestAnimationFrame(() => {
  updateAllHeaderCustomProperties();
});